import React, { useRef, useState } from 'react'
import {
  IconButton,
  Badge,
  Popper,
  Paper,
  ClickAwayListener,
  Box,
  Typography,
  Divider,
  Button,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material'
import NotificationsIcon from '@mui/icons-material/Notifications'
import useNotifications from '../../hooks/useNotifications'
import useAccount from '../../hooks/useAccount'
import useRouter from '../../hooks/useRouter'
import { UserNotification } from '../../types/gotypes'
import { moneyFormatter } from '../../tokenbudgets/model'
import { budgetYearLastDate } from '../../utils/aiAccess'

interface NotificationBellProps {
  customer: string
}

/** Matches agentdeps.NotificationTypeAgentQuestion — an agent is waiting. */
export const NOTIFICATION_TYPE_AGENT_QUESTION = 'agent_question'

/** Matches the alerts package's notification type — an alert rule fired. */
export const NOTIFICATION_TYPE_ALERT_TRIGGERED = 'alert_triggered'

/**
 * An alert's bell line. A colleague's alert that names the viewer as a
 * recipient says whose it is: "From jo@sky.com: Renewals fell to 58%".
 */
export const alertTriggeredSentence = (n: UserNotification, viewerEmail: string): string => {
  const message = n.payload?.message || 'Alert triggered'
  const owner = n.payload?.alert_owner || ''
  return owner && owner.toLowerCase() !== viewerEmail.toLowerCase() ? `From ${owner}: ${message}` : message
}

/** Matches aiaccess's notification type — someone asked for a higher AI access level (#1537 PR D). */
export const NOTIFICATION_TYPE_AI_ACCESS_REQUESTED = 'ai_access_requested'

/** Matches types.NotificationTypeBudgetThreshold — a team crossed one of its alert thresholds (50/75/90/100% until a Manager changes them), or 101, its headroom. */
export const NOTIFICATION_TYPE_BUDGET_THRESHOLD = 'budget_threshold'

/** "UK Insights has used 75% of its AI budget (£7.50 of £10.00)"; at 100%, "… has used all of its AI budget"; at 101 (the headroom is gone too), "… has used its AI budget and its headroom; AI features are stopped". */
export const budgetThresholdSentence = (n: UserNotification): string => {
  const team = n.payload?.team_name || 'A team'
  const threshold = n.payload?.threshold ?? 0
  if (threshold > 100) return `${team} has used its AI budget and its headroom; AI features are stopped`
  if (threshold >= 100) return `${team} has used all of its AI budget`
  // A credits customer's notice says credits (spend control PR 6d); one
  // written before the unit was recorded reads as pounds.
  const money = moneyFormatter(n.payload?.display_unit ? { display_unit: n.payload.display_unit, credit_value_gbp: n.payload.credit_value_gbp ?? 1 } : undefined)
  return `${team} has used ${threshold}% of its AI budget (${money(n.payload?.spent_gbp ?? 0)} of ${money(n.payload?.allocated_gbp ?? 0)})`
}

/** Matches types.NotificationTypeBudgetYearEnd — a team's budget year ends in 30 days, in 7, or ended, with no next year set (annual budgets PR D). */
export const NOTIFICATION_TYPE_BUDGET_YEAR_END = 'budget_year_end'

/**
 * "UK Insights's AI budget year ends on 30 September 2027 (in 30 days)." and,
 * once it has ended, "… ended on 30 September 2027." Then what to do: a
 * Manager sets next year's budget on AI budgets; anyone else (a Realm admin,
 * a team manager) asks Bayes Price, who sets it. With the switch on, an ended
 * year's sentence says the team's AI is off.
 */
export const budgetYearEndSentence = (n: UserNotification, isManager: boolean): string => {
  const team = n.payload?.team_name || 'A team'
  const endAt = n.payload?.end_at
  const day = typeof endAt === 'number' ? budgetYearLastDate(endAt) : ''
  const next = isManager ? "Set next year's budget on AI budgets." : "Bayes Price sets next year's budget."
  if (n.payload?.year_end === 'ended') {
    const off = n.payload?.ai_off ? ' Its AI is off until the next year is set.' : ''
    return `${team}'s AI budget year ended${day ? ` on ${day}` : ''}.${off} ${next}`
  }
  const days = n.payload?.days_left ?? 0
  return `${team}'s AI budget year ends${day ? ` on ${day}` : ''} (in ${days} day${days === 1 ? '' : 's'}). ${next}`
}

/** Matches types.NotificationTypeAnalysisDigest — yesterday's (UTC) flagged advanced analyses, one row per day for a stats reviewer (#1652). */
export const NOTIFICATION_TYPE_ANALYSIS_DIGEST = 'analysis_digest'

/** "3 advanced analyses yesterday (UTC): key driver analysis, regression, by 2 people"; "1 advanced analysis yesterday (UTC): TURF, by 1 person". */
export const analysisDigestSentence = (n: UserNotification): string => {
  const count = n.payload?.flag_count ?? 0
  const people = n.payload?.people ?? 0
  const methods = n.payload?.methods ?? []
  const what = `${count} advanced ${count === 1 ? 'analysis' : 'analyses'} yesterday (UTC)`
  const who = `by ${people} ${people === 1 ? 'person' : 'people'}`
  return `${what}: ${methods.length > 0 ? `${methods.join(', ')}, ` : ''}${who}`
}

/** Matches types.NotificationTypeSocialImport: a first-party social import run needs a Manager (an account paused, the X spend cap reached). Nothing sends it until the import lands (PR 2); the bell shows the row's own text. */
export const NOTIFICATION_TYPE_SOCIAL_IMPORT = 'social_import'

/** Matches types.NotificationTypeReleasePending — a release restarts the app at payload.starts_at (epoch ms); sent to owners of live chats, no link. */
export const NOTIFICATION_TYPE_RELEASE_PENDING = 'release_pending'

/** "<message> Starts at 14:05." */
export const releasePendingSentence = (n: UserNotification): string => {
  const message = n.payload?.message ?? 'Platinum is about to update.'
  const at = n.payload?.starts_at
  if (typeof at !== 'number') return message
  return `${message} Starts at ${new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`
}

/** Matches types.NotificationTypeSprinklrImport — one failure of the daily Sprinklr import, sent to the customer's Managers; payload.message is the sentence, no link. */
export const NOTIFICATION_TYPE_SPRINKLR_IMPORT = 'sprinklr_import'

/** Matches types.NotificationTypeWarehouseTableSyncFailed — a warehouse Tables job's refresh failed; sent to its creator, payload.message is the sentence, share_url the connection page. */
export const NOTIFICATION_TYPE_WAREHOUSE_TABLE_SYNC_FAILED = 'warehouse_table_sync_failed'

/** Matches types.NotificationTypeGuideInvite — a Manager invited the recipient to a guided conversation; share_url is /guided/<customer>/<response_id>. */
export const NOTIFICATION_TYPE_GUIDE_INVITE = 'guide_invite'

/** "jo@bayesprice.com invited you to a guided conversation: Month-end close". */
export const guideInviteSentence = (n: UserNotification): string => {
  const who = n.payload?.sender ? `${n.payload.sender} invited you` : 'You are invited'
  const title = n.payload?.guide_title || ''
  return `${who} to a guided conversation${title ? `: ${title}` : '.'}`
}

/** Matches types.NotificationTypeGuideReviewer — a Manager named the recipient a reviewer of a guide (migration 000190); share_url is /admin/<customer>/guides/<guide_id>. */
export const NOTIFICATION_TYPE_GUIDE_REVIEWER = 'guide_reviewer'

/** "jo@bayesprice.com named you a reviewer of Month-end close." */
export const guideReviewerSentence = (n: UserNotification): string => {
  const who = n.payload?.sender ? `${n.payload.sender} named you` : 'You are'
  const title = n.payload?.guide_title || 'a guided conversation'
  return `${who} a reviewer of ${title}. You can read where each person got to and their notes.`
}

/** The response a guide_invite row opens: payload.response_id, else the id in share_url. */
export const guideInviteResponseId = (n: UserNotification): string => {
  if (n.payload?.response_id) return n.payload.response_id
  const match = (n.payload?.share_url || '').match(/\/guided\/[^/?#]+\/([^/?#]+)/)
  return match ? match[1] : ''
}

/** Matches types.NotificationTypeJobReindexNeeded — a job's tables, variables or uploaded data changed after its search index was built (#2013); one row per Manager and job, resolved by a later build. */
export const NOTIFICATION_TYPE_JOB_REINDEX_NEEDED = 'job_reindex_needed'

const JOB_DATA_CHANGE_LABELS: Record<string, string> = {
  data_upload: 'uploaded data',
  tables: 'tables',
  variables: 'variables',
}

/** "a", "a and b", "a, b and c". */
const listWords = (words: string[]): string =>
  words.length <= 1 ? (words[0] || '') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`

/**
 * "wave3: tables and variables changed since the search index was built. Last
 * change 14:05, 29 Oct." Once a later build covered the changes, "wave3 was
 * re-indexed."
 */
export const jobReindexSentence = (n: UserNotification): string => {
  const job = n.payload?.job || 'A job'
  if (n.payload?.resolved_at) return `${job} was re-indexed.`
  const what = listWords((n.payload?.changes ?? []).map(k => JOB_DATA_CHANGE_LABELS[k] || k)) || 'its data'
  const at = n.payload?.changed_at
  const when = typeof at === 'number'
    ? ` Last change ${new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}, ${new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}.`
    : ''
  return `${job}: ${what} changed since the search index was built.${when}`
}

const formatRelativeTime = (ms: number): string => {
  const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000))
  if (diffSec < 60) return 'just now'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d ago`
}

// NotificationBell is the in-app notification centre (#964 v1): unread
// count in the header, dropdown list, click-through to the dashboard,
// mark-read. Polls on a 60s interval — that IS "real-time" per the approved
// v1 design: the row exists next time the bell loads, no digest.
const NotificationBell: React.FC<NotificationBellProps> = ({ customer }) => {
  const router = useRouter()
  const account = useAccount()
  const { notifications, unreadCount, markRead, markAllRead, handleReindex } = useNotifications({ customer })
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const isRealmAdmin = !!account.user?.role_permissions?.isRealmAdmin
  const isManager = !!account.user?.role_permissions?.isManager
  const viewerEmail = account.user?.session_info?.email || ''

  const handleNotificationClick = (n: UserNotification) => {
    if (!n.read_at) markRead(n.id)
    setOpen(false)
    // An agent waiting on an answer takes you to the session; a shared
    // dashboard takes you to the dashboard. Both arrive as a path in
    // share_url, and each row knows which it is by its type.
    if (n.type === NOTIFICATION_TYPE_AGENT_QUESTION && n.payload?.session_id) {
      router.navigate('agent-session', { customer: n.customer, sessionId: n.payload.session_id })
      return
    }
    if (n.type === NOTIFICATION_TYPE_ALERT_TRIGGERED && n.payload?.alert_rule_id) {
      router.navigate('alert-detail', { customer: n.customer, alertId: n.payload.alert_rule_id })
      return
    }
    // A team manager who is no Realm admin can open only the Teams page, so
    // both admin notices take them there.
    if (n.type === NOTIFICATION_TYPE_AI_ACCESS_REQUESTED) {
      router.navigate(isRealmAdmin ? 'admin-users' : 'admin-teams', { customer: n.customer })
      return
    }
    if (n.type === NOTIFICATION_TYPE_BUDGET_THRESHOLD || n.type === NOTIFICATION_TYPE_BUDGET_YEAR_END) {
      router.navigate(isManager ? 'admin-ai-budgets' : isRealmAdmin ? 'admin-usage' : 'admin-teams', { customer: n.customer })
      return
    }
    // The digest is only sent to a stats reviewer, who may be no Manager or
    // Realm admin: it opens Stats review on the day it summarised.
    if (n.type === NOTIFICATION_TYPE_ANALYSIS_DIGEST) {
      router.navigate('admin-stats-review', { customer: n.customer, from: String(n.payload?.period_start ?? ''), to: String(n.payload?.period_end ?? '') })
      return
    }
    if (n.type === NOTIFICATION_TYPE_WAREHOUSE_TABLE_SYNC_FAILED) {
      const connection = (n.payload?.share_url || '').match(/\/data-connections\/([^/?#]+)/)
      if (connection) router.navigate('admin-data-connection', { customer: n.customer, connectionId: connection[1] })
      return
    }
    if (n.type === NOTIFICATION_TYPE_GUIDE_REVIEWER) {
      const guideId = n.payload?.guide_id || (n.payload?.share_url || '').match(/\/guides\/([^/?#]+)/)?.[1] || ''
      router.navigate(guideId ? 'admin-guide' : 'admin-guides', guideId ? { customer: n.customer, guideId } : { customer: n.customer })
      return
    }
    if (n.type === NOTIFICATION_TYPE_GUIDE_INVITE) {
      const responseId = guideInviteResponseId(n)
      router.navigate(responseId ? 'guided-response' : 'guided', responseId ? { customer: n.customer, responseId } : { customer: n.customer })
      return
    }
    // Search Index is a Manager's page; anyone else just marks the row read.
    if (n.type === NOTIFICATION_TYPE_JOB_REINDEX_NEEDED) {
      if (isManager) router.navigate('admin-search', { customer: n.customer })
      return
    }
    if (n.type === NOTIFICATION_TYPE_RELEASE_PENDING || n.type === NOTIFICATION_TYPE_SPRINKLR_IMPORT) return
    const shareUrl = n.payload?.share_url || ''
    const match = shareUrl.match(/\/dashboard\/([^/?#]+)/)
    if (match) {
      router.navigate('shared-dashboard', { shareId: match[1] })
    }
  }

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={() => setOpen(prev => !prev)}
        sx={{ color: 'rgba(255,255,255,0.65)', '&:hover': { color: 'white' } }}
        aria-label="Notifications"
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" sx={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper elevation={8} sx={{ mt: 0.5, width: 360, maxHeight: 420, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
              <Typography variant="subtitle2">Notifications</Typography>
              {unreadCount > 0 && (
                <Button size="small" onClick={() => markAllRead()} sx={{ textTransform: 'none' }}>
                  Mark all read
                </Button>
              )}
            </Box>
            <Divider />
            {notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No notifications yet.</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {notifications.map(n => (
                  <ListItemButton
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    sx={{ alignItems: 'flex-start', backgroundColor: n.read_at ? 'transparent' : 'action.hover' }}
                  >
                    <ListItemText
                      primary={n.type === NOTIFICATION_TYPE_AGENT_QUESTION
                        ? (n.payload?.session_title || 'Agent session')
                        : n.type === NOTIFICATION_TYPE_ALERT_TRIGGERED
                        ? (n.payload?.alert_name || 'Alert')
                        : n.type === NOTIFICATION_TYPE_AI_ACCESS_REQUESTED
                        ? 'AI access requested'
                        : n.type === NOTIFICATION_TYPE_BUDGET_THRESHOLD || n.type === NOTIFICATION_TYPE_BUDGET_YEAR_END
                        ? 'AI budget'
                        : n.type === NOTIFICATION_TYPE_ANALYSIS_DIGEST
                        ? 'Stats review'
                        : n.type === NOTIFICATION_TYPE_RELEASE_PENDING
                        ? 'Platinum update'
                        : n.type === NOTIFICATION_TYPE_SPRINKLR_IMPORT
                        ? 'Sprinklr import'
                        : n.type === NOTIFICATION_TYPE_WAREHOUSE_TABLE_SYNC_FAILED
                        ? 'Data connections'
                        : n.type === NOTIFICATION_TYPE_GUIDE_INVITE || n.type === NOTIFICATION_TYPE_GUIDE_REVIEWER
                        ? 'Guided conversation'
                        : n.type === NOTIFICATION_TYPE_JOB_REINDEX_NEEDED
                        ? 'Search index'
                        : (n.payload?.dashboard_name || 'Dashboard')}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                            {n.type === NOTIFICATION_TYPE_AGENT_QUESTION
                              ? `Waiting for your answer${n.payload?.message ? `: "${n.payload.message}"` : ''}`
                              : n.type === NOTIFICATION_TYPE_ALERT_TRIGGERED
                              ? alertTriggeredSentence(n, viewerEmail)
                              : n.type === NOTIFICATION_TYPE_AI_ACCESS_REQUESTED
                              ? `${n.payload?.sender} is at level ${n.payload?.requester_level ?? '?'}${n.payload?.feature_label ? ` — wants ${n.payload.feature_label}` : ''}${n.payload?.message ? `: "${n.payload.message}"` : ''}`
                              : n.type === NOTIFICATION_TYPE_BUDGET_THRESHOLD
                              ? budgetThresholdSentence(n)
                              : n.type === NOTIFICATION_TYPE_BUDGET_YEAR_END
                              ? budgetYearEndSentence(n, isManager)
                              : n.type === NOTIFICATION_TYPE_ANALYSIS_DIGEST
                              ? analysisDigestSentence(n)
                              : n.type === NOTIFICATION_TYPE_RELEASE_PENDING
                              ? releasePendingSentence(n)
                              : n.type === NOTIFICATION_TYPE_SPRINKLR_IMPORT
                              ? (n.payload?.message || 'The Sprinklr import failed.')
                              : n.type === NOTIFICATION_TYPE_WAREHOUSE_TABLE_SYNC_FAILED
                              ? (n.payload?.message || 'Refreshing a warehouse table failed.')
                              : n.type === NOTIFICATION_TYPE_GUIDE_INVITE
                              ? guideInviteSentence(n)
                              : n.type === NOTIFICATION_TYPE_GUIDE_REVIEWER
                              ? guideReviewerSentence(n)
                              : n.type === NOTIFICATION_TYPE_JOB_REINDEX_NEEDED
                              ? jobReindexSentence(n)
                              : `${n.payload?.sender} shared this dashboard${n.payload?.message ? `: "${n.payload.message}"` : ''}`}
                          </Typography>
                          <Typography component="span" variant="caption" color="text.secondary">
                            {formatRelativeTime(n.created_at)}
                          </Typography>
                          {n.type === NOTIFICATION_TYPE_JOB_REINDEX_NEEDED && isManager && !n.payload?.resolved_at && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="info"
                              aria-label={`Re-index ${n.payload?.job || ''}`}
                              onClick={(e) => { e.stopPropagation(); handleReindex(n) }}
                              sx={{ display: 'block', mt: 0.5, textTransform: 'none' }}
                            >
                              Re-index
                            </Button>
                          )}
                        </>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  )
}

export default NotificationBell
